import { useState, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, Clock, User, Linkedin, MessageCircle, Twitter, Link2, Check, ChevronDown } from "lucide-react";
import { BLOG_POSTS } from "@/lib/blog-posts";

const SITE = "https://kentro.com.br";
const URL = `${SITE}/blog/hiperpersonalizacao`;
const H1 = "Hiperpersonalização: o que é, como funciona e por que muda a forma de atender";
const TITLE = "Hiperpersonalização: o que é, como funciona e por que muda a forma de atender | Kentro";
const DESC =
  "Hiperpersonalização é o conceito que faz o sistema se adaptar ao seu negócio — e não o contrário. Entenda como a Kentro usa IA, extensões e MCP para criar experiências únicas em cada atendimento.";
const OG_TITLE = "Hiperpersonalização: quando o sistema se adapta ao seu negócio | Kentro";
const OG_DESC =
  "Descubra o conceito que está mudando a forma como empresas atendem seus clientes. Hiperpersonalização vai além da personalização: é o sistema que se adapta à sua operação.";
const IMG = `${SITE}/og-image.png`;
const SUBTITLE = "Não é você que se adapta ao sistema. É o sistema que se adapta ao seu negócio.";

const FAQS = [
  {
    q: "O que é hiperpersonalização?",
    a: "Hiperpersonalização é a capacidade de um sistema se adaptar à operação de cada negócio — suas especialidades, regras, processos e canais — em vez de exigir que a empresa se adapte ao software. É um conceito criado pela Kentro para descrever a forma como sua plataforma opera: construindo extensões sob medida e conectando a IA ao contexto real de cada cliente.",
  },
  {
    q: "Qual a diferença entre personalização e hiperpersonalização?",
    a: "Personalização tradicional é cosmética: trocar o nome no e-mail, segmentar uma lista, escolher uma cor no painel. Hiperpersonalização é estrutural: o sistema muda a forma como funciona para cada negócio. Em vez de \"Olá, João\", o sistema sabe que o João tem convênio X, prefere consultas às terças e já fez três exames no último ano.",
  },
  {
    q: "A hiperpersonalização funciona para empresas pequenas?",
    a: "Sim. Na Kentro, a hiperpersonalização não exige time de TI nem projeto de desenvolvimento. As extensões são construídas dentro da plataforma, e a configuração é feita pela própria equipe de implantação. Uma clínica com dois médicos se beneficia tanto quanto uma rede com vinte unidades.",
  },
  {
    q: "O que é MCP e por que ele importa?",
    a: "MCP (Model Context Protocol) é um padrão aberto que funciona como uma tomada universal: conecta a inteligência artificial a qualquer ferramenta ou base de dados. Na Kentro, o MCP permite que a IA acesse extensões, leia dados do negócio e tome decisões contextuais — sem precisar de integração técnica complexa.",
  },
  {
    q: "A IA da Kentro substitui a equipe de atendimento?",
    a: "Não. A IA cuida das tarefas repetitivas e dos atendimentos que seguem um padrão — como agendamentos, consultas de status e montagem de carrinhos. Quando o caso exige julgamento humano, a IA encaminha para a equipe com o contexto completo. O atendente decide; a IA prepara.",
  },
  {
    q: "Em quais setores a hiperpersonalização se aplica?",
    a: "Hoje, a Kentro aplica hiperpersonalização em clínicas médicas e odontológicas, farmácias, clínicas de estética, provedores de internet, escolas, universidades, e-commerce e empresas de serviços. Qualquer negócio que atenda clientes por múltiplos canais e tenha regras próprias de operação pode se beneficiar.",
  },
  {
    q: "Hiperpersonalização tem a ver com LGPD?",
    a: "A hiperpersonalização usa dados que o cliente fornece durante o atendimento — como convênio, preferências e histórico de compras. A Kentro trata esses dados conforme a LGPD, usando-os exclusivamente para melhorar o atendimento daquele cliente, dentro da plataforma do próprio negócio.",
  },
];

const TOC = [
  { id: "atendimento-de-ninguem", label: "O atendimento de todo mundo virou o atendimento de ninguém" },
  { id: "o-que-e", label: "O que é hiperpersonalização" },
  { id: "origem", label: "De onde veio esse conceito" },
  { id: "na-pratica", label: "Como a hiperpersonalização funciona na prática" },
  { id: "diferenca", label: "Hiperpersonalização vs. personalização" },
  { id: "tecnologia", label: "A tecnologia por trás: MCP, extensões e IA contextual" },
  { id: "numeros", label: "Os números que sustentam a hiperpersonalização" },
  { id: "para-quem", label: "Para quem a hiperpersonalização faz sentido" },
  { id: "o-que-muda", label: "O que muda quando o sistema se adapta" },
  { id: "faq", label: "Perguntas frequentes" },
];

const RELATED = [
  "whatsapp-business-pago-app-ou-api-quando-migrar-pmes",
  "como-automatizar-atendimento-whatsapp",
  "nem-todo-atendimento-precisa-de-ia-generativa-dados-antes-do-agente-sap-pmes",
];

const KEYWORDS =
  "hiperpersonalização, personalização, atendimento ao cliente, CRM, WhatsApp Business, inteligência artificial, IA para empresas, MCP, omnichannel, Kentro, automação de atendimento, sistema para clínicas, sistema para farmácias";

export const Route = createFileRoute("/blog/hiperpersonalizacao")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: KEYWORDS },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: OG_TITLE },
      { property: "og:description", content: OG_DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:image", content: IMG },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: OG_TITLE },
      { name: "twitter:description", content: OG_DESC },
      { name: "twitter:image", content: IMG },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: H1,
          description:
            "Hiperpersonalização é o conceito que faz o sistema se adaptar ao seu negócio — e não o contrário. Entenda como a Kentro usa IA, extensões e MCP para criar experiências únicas.",
          author: { "@type": "Organization", name: "Kentro", url: SITE },
          publisher: {
            "@type": "Organization",
            name: "Kentro",
            logo: { "@type": "ImageObject", url: IMG },
          },
          image: IMG,
          datePublished: "2026-10-19",
          dateModified: "2026-10-19",
          mainEntityOfPage: URL,
          inLanguage: "pt-BR",
          keywords: "hiperpersonalização, personalização, CRM, WhatsApp Business, inteligência artificial, MCP, omnichannel, Kentro",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
            { "@type": "ListItem", position: 3, name: "Hiperpersonalização", item: URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Kentro",
          url: SITE,
          logo: IMG,
        }),
      },
    ],
  }),
  component: HiperPage,
});

const A = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} className="font-medium text-hp-purple underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-hp-purple">
    {children}
  </a>
);
const S = ({ children }: { children: ReactNode }) => <strong className="font-semibold text-hp-ink">{children}</strong>;
const H2 = ({ id, children }: { id: string; children: ReactNode }) => (
  <h2 id={id} className="mt-14 scroll-mt-24 text-2xl font-bold leading-tight tracking-tight text-hp-purple md:text-3xl">
    {children}
  </h2>
);
const H3 = ({ children }: { children: ReactNode }) => (
  <h3 className="mt-8 text-xl font-semibold text-hp-ink">{children}</h3>
);
const P = ({ children }: { children: ReactNode }) => <p className="mt-5">{children}</p>;
const UL = ({ children }: { children: ReactNode }) => (
  <ul className="mt-5 list-disc space-y-2 pl-6 marker:text-hp-purple">{children}</ul>
);
const Callout = ({ children, label }: { children: ReactNode; label: string }) => (
  <aside aria-label={label} className="mt-6 rounded-2xl border border-hp-mint bg-hp-mint-soft p-6">
    <span className="font-mono-tech text-xs font-medium uppercase tracking-wider text-hp-purple">{label}</span>
    <div className="mt-2">{children}</div>
  </aside>
);

function ShareBar() {
  const [copied, setCopied] = useState(false);
  const text = encodeURIComponent(H1);
  const u = encodeURIComponent(URL);
  const btn =
    "inline-flex size-10 items-center justify-center rounded-full border border-hp-surface/30 text-hp-surface transition hover:bg-hp-surface/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hp-mint";
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2">
      <span className="mr-1 text-sm text-hp-surface/85">Compartilhar:</span>
      <a className={btn} href={`https://www.linkedin.com/sharing/share-offsite/?url=${u}`} target="_blank" rel="noopener noreferrer" aria-label="Compartilhar no LinkedIn">
        <Linkedin className="size-4" />
      </a>
      <a className={btn} href={`https://api.whatsapp.com/send?text=${text}%20${u}`} target="_blank" rel="noopener noreferrer" aria-label="Compartilhar no WhatsApp">
        <MessageCircle className="size-4" />
      </a>
      <a className={btn} href={`https://twitter.com/intent/tweet?text=${text}&url=${u}`} target="_blank" rel="noopener noreferrer" aria-label="Compartilhar no X (Twitter)">
        <Twitter className="size-4" />
      </a>
      <button
        type="button"
        className={btn}
        aria-label={copied ? "Link copiado" : "Copiar link"}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(URL);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            /* clipboard indisponível */
          }
        }}
      >
        {copied ? <Check className="size-4" /> : <Link2 className="size-4" />}
      </button>
      <span aria-live="polite" className="text-sm text-hp-mint">{copied ? "Link copiado!" : ""}</span>
    </div>
  );
}

function TocList() {
  return (
    <ol className="space-y-2 text-sm">
      {TOC.map((t) => (
        <li key={t.id}>
          <a href={`#${t.id}`} className="block rounded px-1 text-hp-ink/80 hover:text-hp-purple focus-visible:outline-2 focus-visible:outline-hp-purple">
            {t.label}
          </a>
        </li>
      ))}
    </ol>
  );
}

function HiperPage() {
  const related = RELATED.map((s) => BLOG_POSTS.find((p) => p.slug === s)).filter(Boolean) as typeof BLOG_POSTS;
  return (
    <div className="bg-hp-bg font-poppins text-hp-ink">
      {/* Capa / Hero */}
      <header className="hp-cover-gradient pt-24 pb-14 text-hp-surface">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Trilha de navegação" className="text-sm text-hp-surface/80">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-hp-mint">Home</Link></li>
              <li aria-hidden>›</li>
              <li><Link to="/blog" className="underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-hp-mint">Blog</Link></li>
              <li aria-hidden>›</li>
              <li aria-current="page" className="text-hp-surface">Hiperpersonalização</li>
            </ol>
          </nav>
          <span className="mt-6 inline-flex rounded-full border border-hp-mint/60 px-3 py-1 font-mono-tech text-xs font-medium uppercase tracking-wider text-hp-mint">
            Tecnologia &amp; Inovação
          </span>
          <h1 className="mt-4 max-w-4xl text-3xl font-bold leading-tight tracking-tight md:text-5xl">{H1}</h1>
          <p className="mt-4 max-w-3xl text-lg italic text-hp-surface/90">{SUBTITLE}</p>
          <p className="mt-3 max-w-3xl text-base text-hp-surface/80">{DESC}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-hp-surface/85">
            <span className="inline-flex items-center gap-1.5"><Calendar className="size-4" /> <time dateTime="2026-10-19">19 de outubro de 2026</time></span>
            <span className="inline-flex items-center gap-1.5"><Clock className="size-4" /> 12 min de leitura</span>
            <span className="inline-flex items-center gap-1.5"><User className="size-4" /> Equipe Kentro</span>
          </div>
          <ShareBar />
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-10 px-4 py-12 sm:px-6 lg:px-8">
        <aside className="hidden w-60 shrink-0 md:block">
          <nav aria-label="Índice do artigo" className="sticky top-24 rounded-2xl border border-hp-purple/15 bg-hp-surface p-5">
            <p className="mb-3 font-mono-tech text-xs font-medium uppercase tracking-wider text-hp-purple">Índice</p>
            <TocList />
          </nav>
        </aside>

        <article className="mx-auto w-full max-w-[720px] text-[18px] leading-[1.75]">
          <details className="mb-8 rounded-2xl border border-hp-purple/15 bg-hp-surface p-4 md:hidden">
            <summary className="flex cursor-pointer items-center justify-between font-mono-tech text-sm font-medium text-hp-purple">
              Índice do artigo <ChevronDown className="size-4" />
            </summary>
            <nav aria-label="Índice do artigo (mobile)" className="mt-3"><TocList /></nav>
          </details>

          <H2 id="atendimento-de-ninguem">O atendimento de todo mundo virou o atendimento de ninguém</H2>
          <P>São 23h47. A Marina — depois de meses adiando — decide marcar uma consulta. Ela abre o WhatsApp, escreve para a clínica e espera. Ninguém responde. A recepção está dormindo, e isso é normal: o expediente acabou há horas.</P>
          <P>De manhã, a Marina já marcou em outra clínica.</P>
          <P>Essa história se repete milhares de vezes por dia, em clínicas, farmácias, escritórios e lojas de todo o Brasil. Segundo a Doctoralia, <S>33% dos agendamentos no Brasil acontecem fora do horário comercial</S>. A McKinsey aponta que a personalização bem implementada <S>aumenta a satisfação do cliente em 15% a 20%</S>. E um estudo da Twilio revela que <S>71% dos consumidores deixam de comprar quando a experiência não é relevante para eles</S>.</P>
          <P>O problema não é a equipe. Não é falta de esforço. É que os sistemas que deveriam ajudar foram construídos de forma genérica — e esperam que toda empresa funcione do mesmo jeito.</P>
          <P>É aqui que entra um conceito que muda essa lógica: a <S>hiperpersonalização</S>.</P>

          <H2 id="o-que-e">O que é hiperpersonalização</H2>
          <Callout label="Definição">
            <blockquote className="border-l-4 border-hp-purple pl-4">
              <S>Hiperpersonalização</S> é a capacidade de um sistema se adaptar à operação de cada negócio — suas especialidades, suas regras, seus processos, seus canais — em vez de forçar a empresa a operar do jeito que o software foi programado.
            </blockquote>
          </Callout>
          <P>O termo, cunhado pela <S>Kentro</S>, vai além do conceito clássico de "personalização". Personalizar, no sentido tradicional, é trocar o nome no e-mail, segmentar uma lista, escolher uma cor no painel. É cosmético. Não muda a estrutura.</P>
          <P>A hiperpersonalização é estrutural. Ela acontece quando o sistema entende que uma clínica de dermatologia funciona de forma diferente de uma clínica ortopédica. Que uma farmácia de manipulação tem regras de sugestão de venda diferentes de uma drogaria de bairro. Que um provedor de internet no interior do Pará tem um fluxo de atendimento que não tem nada a ver com o de uma operadora em São Paulo.</P>
          <P>Em outras palavras: <S>o sistema se adapta à sua empresa, e não o contrário</S>.</P>

          <H2 id="origem">De onde veio esse conceito</H2>
          <P>A ideia não surgiu de uma teoria. Surgiu de um problema real.</P>
          <P>A Kentro atende empresas em setores muito diferentes — de farmácias a provedores de internet, de clínicas médicas a redes de estética. Cada uma chega com processos próprios, regras específicas, integrações que o sistema "padrão" de atendimento simplesmente não previu.</P>
          <P>A resposta convencional do mercado para isso é uma de duas:</P>
          <ol className="mt-5 list-decimal space-y-2 pl-6 marker:font-semibold marker:text-hp-purple">
            <li><S>Forçar a adaptação.</S> O sistema funciona de um jeito, e a empresa que se adeque.</li>
            <li><S>Cobrar caro por personalização.</S> Cada ajuste vira um projeto de TI com escopo, prazo e orçamento próprios.</li>
          </ol>
          <P>A Kentro escolheu um caminho diferente: construir uma plataforma que <S>permite criar pequenos sistemas dentro dela mesma</S> — extensões que se plugam na operação de cada cliente — e integrar tudo via um padrão aberto chamado <S>MCP (Model Context Protocol)</S>.</P>
          <P>Com o MCP, a <A href="/ia">inteligência artificial da Kentro</A> enxerga os dados dessas extensões. Ela não é uma IA genérica treinada para qualquer coisa: ela é uma IA que entende o contexto específico daquela empresa, daquele atendimento, daquele cliente.</P>
          <P>A isso, a Kentro deu o nome de <S>hiperpersonalização</S>.</P>

          <H2 id="na-pratica">Como a hiperpersonalização funciona na prática</H2>
          <P>Para entender o impacto, vale acompanhar três cenários reais — cada um em um setor diferente.</P>
          <H3>1. Na clínica: atendimento que não dorme</H3>
          <P>A Marina escreve para a clínica às 23h47. A IA da Kentro responde na hora: pergunta o convênio, mostra os horários livres do médico certo e confirma a consulta. Se o caso foge do padrão — um procedimento que exige autorização prévia, por exemplo — a conversa vai para a recepção no dia seguinte, com o contexto pronto: quem é a paciente, qual o convênio e o que ela pediu.</P>
          <P>Cada clínica configura <S>suas especialidades, seus convênios, suas agendas e suas regras</S>. Uma clínica de pediatria não funciona como uma clínica de ortopedia — e o sistema sabe disso.</P>
          <H3>2. Na farmácia: venda assistida por IA</H3>
          <P>O Seu Carlos manda uma mensagem pedindo um aparelho de barbear. O copiloto de IA da Kentro lê a conversa, consulta o histórico do cliente e percebe: ele compra lâmina todo mês, nunca leva o gel de barbear e sempre volta depois para buscar. A IA monta o carrinho com a sugestão. O atendente confere e, se concordar, manda para o cliente aprovar.</P>
          <P>Cada farmácia configura <S>seu catálogo, suas regras de sugestão e seus combos</S>. O copiloto aprende com cada venda.</P>
          <P>Segundo a McKinsey, empresas que investem em personalização <S>geram de 10% a 15% a mais de receita</S>.</P>
          <H3>3. No provedor de internet: suporte que resolve sem escalar</H3>
          <P>O João liga para o suporte do provedor. A IA da Kentro já sabe o plano dele, a região, o histórico de chamados e a última interrupção na rede daquela torre. Antes de transferir para o técnico, ela tenta resolver: reinicia o roteador remotamente, verifica se há manutenção programada, envia um link de teste de velocidade.</P>
          <P>Cada provedor configura <S>suas torres, seus planos, seus scripts de atendimento e suas regras de escalonamento</S>. Um provedor em Belém não opera como um em Curitiba.</P>

          <H2 id="diferenca">Hiperpersonalização vs. personalização: qual a diferença?</H2>
          <div className="mt-6 overflow-x-auto rounded-2xl border border-hp-purple/15 bg-hp-surface">
            <table className="w-full min-w-[620px] border-collapse text-left text-base">
              <caption className="sr-only">Comparativo entre personalização tradicional e hiperpersonalização</caption>
              <thead className="bg-hp-purple text-hp-surface">
                <tr>
                  <th scope="col" className="p-3 font-semibold">Aspecto</th>
                  <th scope="col" className="p-3 font-semibold">Personalização tradicional</th>
                  <th scope="col" className="p-3 font-semibold">Hiperpersonalização (Kentro)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["O que muda", "A aparência (nome, cor, logo)", "A estrutura do sistema"],
                  ["Quem se adapta", "A empresa se adapta ao software", "O software se adapta à empresa"],
                  ["Dados usados", "Segmentação demográfica", "Contexto em tempo real + histórico + regras do negócio"],
                  ["IA", "Genérica ou inexistente", "Conectada ao contexto do negócio via MCP"],
                  ["Extensões", "Não previstas", "Sistemas construídos dentro da plataforma"],
                  ["Escala", "Funciona igual para todos", "Cada empresa tem sua configuração"],
                  ["Custo de adaptação", "Projeto de TI caro e lento", "Configuração nativa, sem código"],
                  ["Exemplo", "\"Olá, {nome}!\" no e-mail", "IA que sabe o convênio, o horário do médico e a regra da clínica"],
                ].map(([a, b, c]) => (
                  <tr key={a} className="border-t border-hp-purple/10 align-top">
                    <th scope="row" className="p-3 font-semibold text-hp-ink">{a}</th>
                    <td className="p-3">{a === "Exemplo" ? <code className="font-mono-tech text-sm">{b}</code> : b}</td>
                    <td className="p-3">{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <H2 id="tecnologia">A tecnologia por trás: MCP, extensões e IA contextual</H2>
          <P>Para que a hiperpersonalização funcione, três peças se conectam:</P>
          <H3>MCP (Model Context Protocol)</H3>
          <P>Pense em uma <S>tomada universal</S>. O MCP é um padrão aberto que conecta a inteligência artificial a qualquer ferramenta ou base de dados. Em vez de construir uma integração do zero para cada sistema, o MCP permite que a IA "plugue" em uma extensão, leia os dados e tome decisões com base neles.</P>
          <P>Na Kentro, isso significa que a IA de uma farmácia pode acessar o catálogo de produtos, o histórico de compras e as regras de sugestão — tudo pelo mesmo padrão. E uma clínica pode dar à IA acesso às agendas dos médicos, aos convênios aceitos e às regras de prioridade.</P>
          <H3>Extensões</H3>
          <P>As extensões são <S>pequenos sistemas construídos dentro da Kentro</S>. Em vez de depender de um desenvolvedor externo para criar uma funcionalidade, a Kentro monta a extensão que o cliente precisa — e ela passa a fazer parte da operação.</P>
          <P>Exemplos:</P>
          <UL>
            <li>Uma extensão de agendamento inteligente para clínicas</li>
            <li>Uma extensão de carrinho com sugestão de venda para farmácias</li>
            <li>Uma extensão de controle de torres e manutenção para provedores</li>
            <li>Uma extensão de orçamento automatizado para clínicas de estética</li>
          </UL>
          <H3>IA contextual</H3>
          <P>A IA da Kentro não é um chatbot genérico. Ela opera <S>dentro do contexto de cada negócio</S>. Isso significa que ela sabe:</P>
          <UL>
            <li>Quais são as especialidades da clínica</li>
            <li>Quais convênios são aceitos</li>
            <li>Qual médico atende às quartas-feiras de manhã</li>
            <li>Que o Seu Carlos compra lâmina todo mês</li>
            <li>Que a torre X do provedor está em manutenção programada</li>
          </UL>
          <P>Quando a IA não sabe resolver, ela encaminha para a <A href="/gestao-de-atendimento">equipe humana</A> — com o contexto pronto. O atendente não começa do zero.</P>

          <H2 id="numeros">Os números que sustentam a hiperpersonalização</H2>
          <Callout label="Estatísticas">
            <p>Os dados de mercado confirmam que a personalização profunda não é tendência — é necessidade:</p>
            <ul className="mt-3 space-y-2">
              <li><S><span className="font-mono-tech">71%</span></S> dos consumidores deixam de comprar quando a experiência não é personalizada (Twilio)</li>
              <li><S><span className="font-mono-tech">15% a 20%</span></S> de aumento na satisfação do cliente com personalização baseada em IA (McKinsey)</li>
              <li><S><span className="font-mono-tech">10% a 15%</span></S> a mais de receita para empresas que investem em personalização (McKinsey)</li>
              <li><S><span className="font-mono-tech">93%</span></S> dos profissionais de marketing afirmam que personalização melhora métricas de vendas (HubSpot)</li>
              <li><S><span className="font-mono-tech">87%</span></S> das marcas planejam aumentar o investimento em personalização em 2026 (StackAdapt)</li>
              <li><S><span className="font-mono-tech">33%</span></S> dos agendamentos médicos no Brasil acontecem fora do horário comercial (Doctoralia)</li>
            </ul>
          </Callout>
          <p className="mt-3 text-sm text-hp-ink/75">Fontes: Doctoralia, McKinsey, Twilio, HubSpot, StackAdapt</p>
          <P>A diferença entre "personalizar" e <S>hiperpersonalizar</S> é que, no segundo caso, a adaptação não é cosmética — ela muda a forma como o sistema opera para cada cliente.</P>

          <H2 id="para-quem">Para quem a hiperpersonalização faz sentido</H2>
          <P>A hiperpersonalização não é exclusiva de grandes corporações com time de TI interno. Na Kentro, ela foi projetada para empresas que:</P>
          <UL>
            <li><S>Atendem pelo WhatsApp</S> e precisam que o sistema entenda o contexto de cada conversa</li>
            <li><S>Operam em setores com regras específicas</S> — saúde, educação, varejo, serviços, telecomunicações</li>
            <li><S>Têm processos que não cabem em um sistema genérico</S> — e estão cansadas de adaptar sua operação ao software</li>
            <li><S>Querem usar IA, mas precisam que ela entenda o negócio</S> — não apenas responda perguntas genéricas</li>
            <li><S>Buscam escalar o atendimento sem perder qualidade</S> — automatizar sem despersonalizar</li>
          </UL>
          <P>Hoje, a Kentro aplica hiperpersonalização em setores como:</P>
          <ul className="mt-5 space-y-2 pl-1">
            <li>🏥 Clínicas médicas e odontológicas</li>
            <li>💊 Farmácias e drogarias</li>
            <li>💆 Clínicas de estética</li>
            <li>🌐 Provedores de internet</li>
            <li>🏫 Escolas e universidades</li>
            <li>🛒 E-commerce e lojas de varejo</li>
            <li>🏢 Empresas de serviços e consultorias</li>
          </ul>
          <p className="mt-4 text-base text-hp-ink/80">
            Veja <A href="/cases">resultados por segmento</A>, as <A href="/funcionalidades">funcionalidades da plataforma</A> e o <A href="/omnichannel">atendimento omnichannel</A>.
          </p>

          <H2 id="o-que-muda">O que muda quando o sistema se adapta</H2>
          <P>A hiperpersonalização não é uma funcionalidade. É uma filosofia de produto. E ela muda três coisas na prática:</P>
          <P><S>1. O tempo de resposta deixa de depender do expediente.</S> Se a IA entende o contexto do negócio, ela pode responder às 23h47 — com a mesma precisão de um atendente treinado.</P>
          <P><S>2. A equipe trabalha com contexto, não com suposição.</S> Quando a IA encaminha um caso para o humano, o contexto vai junto. O atendente sabe quem é o cliente, o que ele pediu e qual é a urgência.</P>
          <P><S>3. Cada venda vira uma oportunidade de venda maior.</S> Se o sistema conhece o histórico e as preferências do cliente, ele pode sugerir — e o atendente decide. A venda assistida por IA não empurra: ela lembra.</P>

          <H2 id="faq">Perguntas frequentes sobre hiperpersonalização</H2>
          <div className="mt-6 space-y-3">
            {FAQS.map((f) => (
              <details key={f.q} className="group rounded-2xl border border-hp-purple/15 bg-hp-surface p-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded font-semibold text-hp-ink focus-visible:outline-2 focus-visible:outline-hp-purple">
                  <h3 className="text-lg">{f.q}</h3>
                  <ChevronDown className="size-5 shrink-0 text-hp-purple transition group-open:rotate-180" aria-hidden />
                </summary>
                <p className="mt-3 text-base">{f.a}</p>
              </details>
            ))}
          </div>

          <section aria-labelledby="cta-hp" className="hp-cta-gradient mt-14 rounded-3xl p-8 text-hp-surface md:p-10">
            <h2 id="cta-hp" className="text-2xl font-bold md:text-3xl">Quer ver a hiperpersonalização funcionando?</h2>
            <p className="mt-3 text-lg font-semibold text-hp-mint">Cada negócio é diferente. A Kentro se adapta ao seu.</p>
            <p className="mt-3 text-base text-hp-surface/90">Peça uma demonstração gratuita e veja como a hiperpersonalização funciona no seu setor — com as suas regras, os seus canais e os seus processos.</p>
            <Link to="/contato" className="mt-6 inline-flex rounded-full bg-hp-mint px-6 py-3 font-semibold text-hp-ink transition hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hp-surface">
              Peça uma demonstração gratuita →
            </Link>
            <p className="mt-4 font-mono-tech text-sm text-hp-surface/80">kentro.com.br</p>
          </section>

          <section aria-labelledby="relacionados" className="mt-14">
            <h2 id="relacionados" className="text-2xl font-bold text-hp-purple">Artigos relacionados</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {related.map((p) => (
                <Link key={p.slug} to="/blog/$slug" params={{ slug: p.slug }} className="flex flex-col rounded-2xl border border-hp-purple/15 bg-hp-surface p-5 text-base transition hover:border-hp-purple focus-visible:outline-2 focus-visible:outline-hp-purple">
                  <span className="font-mono-tech text-xs uppercase text-hp-purple">{p.category}</span>
                  <h3 className="mt-2 font-semibold leading-snug text-hp-ink">{p.title}</h3>
                  <span className="mt-auto pt-3 text-sm font-semibold text-hp-purple">Ler artigo →</span>
                </Link>
              ))}
            </div>
          </section>
        </article>
      </div>
    </div>
  );
}
